/**
 * Firebase Remote Config wrapper.
 *
 * Provides `fetchRemoteConfig` (with exponential-backoff handling for
 * `FETCH_THROTTLE` errors) and a simple `getRemoteConfigValue` accessor.
 */

import { fetchAndActivate, getValue, type Value } from 'firebase/remote-config';
import { remoteConfig } from './client';

const BACKOFF_MAX_MS = 3_600_000; // 1 hour

/**
 * Fetch and activate remote config values.
 *
 * On a `FETCH_THROTTLE` error the retry count is persisted to localStorage so
 * that subsequent calls apply exponential backoff (same pattern as the
 * original app).
 *
 * @returns `true` when new values were activated, `false` otherwise.
 */
export async function fetchRemoteConfig(): Promise<boolean> {
  if (!remoteConfig) throw new Error('Remote Config not initialised');

  // Honour a previous backoff before hitting the wire
  const lastRetry = getLastRetry();
  const retryCount = getRetryCount();
  const backoffMs = Math.min(1_000 * 2 ** retryCount, BACKOFF_MAX_MS);

  if (lastRetry && Date.now() - lastRetry < backoffMs) {
    // Still in the backoff window — skip this attempt
    return false;
  }

  try {
    const activated = await fetchAndActivate(remoteConfig);
    clearRetryInfo();
    return activated;
  } catch (err: unknown) {
    const fbErr = err as { code?: string };
    if (fbErr.code === 'FETCH_THROTTLE') {
      storeRetryInfo(retryCount);
    }
    console.error('Remote config fetch failed:', err);
    throw err;
  }
}

/**
 * Get a remote config value by key.
 *
 * @returns The `Value` object from the Firebase SDK.
 */
export function getRemoteConfigValue(key: string): Value {
  if (!remoteConfig) throw new Error('Remote Config not initialised');
  return getValue(remoteConfig, key);
}

/* ------------------------------------------------------------------ */
/*  Backoff helpers (localStorage)                                     */
/* ------------------------------------------------------------------ */

const LS_RETRY_KEY = 'remoteConfigRetryCount';
const LS_LAST_KEY = 'remoteConfigLastRetry';

function getRetryCount(): number {
  try {
    const raw = localStorage.getItem(LS_RETRY_KEY);
    return raw ? Number.parseInt(raw, 10) : 0;
  } catch {
    return 0;
  }
}

function getLastRetry(): number {
  try {
    const raw = localStorage.getItem(LS_LAST_KEY);
    return raw ? Number.parseInt(raw, 10) : 0;
  } catch {
    return 0;
  }
}

function storeRetryInfo(currentCount: number): void {
  try {
    localStorage.setItem(LS_LAST_KEY, String(Date.now()));
    localStorage.setItem(LS_RETRY_KEY, String(currentCount + 1));
  } catch {
    // localStorage may be unavailable (SSR, privacy mode, …)
  }
}

function clearRetryInfo(): void {
  try {
    localStorage.removeItem(LS_LAST_KEY);
    localStorage.removeItem(LS_RETRY_KEY);
  } catch {
    // best-effort
  }
}
